// src/Contexts/ClientContext.js
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import {
  getClients,
  addClient as addClientService,
  getClientById as getClientByIdService,
  updateClient as updateClientService,
  getFilteredSubmissions as getFilteredSubmissionsService,
  fetchExecutives
} from '../../Services/salesapis'; // Corrected the path
import LOCAL_KEYS, { parseLocalStorageJSON } from '../../helpers/localstorage';

// Create Context
export const ClientContext = createContext();

// Provider Component
export const ClientProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // For better error handling
  const [executiveMap, setExecutiveMap] = useState({}); // To store ID-to-name mapping

  // Fetch all clients
  const fetchClients = async () => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      setLoading(false);
      return;
    }

    // Skip API call for recruiters
    const userDetails = parseLocalStorageJSON(LOCAL_KEYS.USER_DETAILS);
    if (userDetails?.acctType === "RECRUITER") {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null); // Reset error state on new fetch attempt
    try {
      const data = await getClients();
      setClients(data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setError('Failed to fetch clients'); // Set error state
    } finally {
      setLoading(false);
    }
  };

  // Fetch filtered submissions
  const fetchFilteredSubmissions = async (filters) => { 
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      setLoading(false);
      return { data: [], message: "Password expired", totalElements: 0, totalPages: 0 };
    }

    setLoading(true); 
    setError(null);
    
    try { 
      const response = await getFilteredSubmissionsService(filters); 

      const fetchedData = response.data || [];
      const message = response.message;
      const totalElements = response.totalElements || 0;
      const totalPages = response.totalPages || 0;

      // Ensure submissions is always an array and map IDs to full names
      const submissionsWithFullNames = Array.isArray(fetchedData) ? fetchedData.map(submission => ({
        ...submission,
        submittedBy: executiveMap[submission.submittedBy] || submission.submittedBy // Use mapped name or original ID
      })) : [];
      setSubmissions(submissionsWithFullNames); 
      
      // Return data, message, and pagination info
      return { data: submissionsWithFullNames, message, totalElements, totalPages };

    } catch (error) { 
      console.error('Failed to fetch filtered submissions:', error); 
      setError('Failed to fetch filtered submissions');
      setSubmissions([]); // Reset to empty array on error
      // Return empty data and an error message
      return { data: [], message: error.message || "Failed to fetch submissions.", totalElements: 0, totalPages: 0 }; 
    } finally { 
      setLoading(false); 
    } 
  };

  // Add a new client
  const addNewClient = async (clientData) => {
    try {
      const newClient = await addClientService(clientData);
      // Update submissions with the new client
      setSubmissions(prev => Array.isArray(prev) ? [...prev, newClient] : [newClient]);
      return newClient;
    } catch (error) {
      console.error('Failed to add client:', error);
      throw error;
    }
  };

  // Update an existing client
  const updateExistingClient = async (id, clientData) => {
    try {
      const updatedClient = await updateClientService(id, clientData);
      
      // Update local state first
      setSubmissions(prev => 
        Array.isArray(prev) 
          ? prev.map(client => client.id === id ? updatedClient : client)
          : []
      );
      
      // Then fetch fresh data with filters
      await fetchFilteredSubmissions({
        company: clientData.company,
        employees: clientData.tempEmpId,
        submittedBy: clientData.submittedBy,
      });
      
      return updatedClient;
    } catch (error) {
      console.error('Failed to update client:', error);
      setError('Failed to update client');
      throw error;
    }
  };

  // Fetch client by ID
  const fetchClientById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getClientByIdService(id);
      setClient(data);
      return data;
    } catch (error) {
      console.error('Failed to fetch client by ID:', error);
      setError('Failed to fetch client by ID');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch executives and create a map
  useEffect(() => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      return;
    }

    // Skip API call for recruiters
    const userDetails = parseLocalStorageJSON(LOCAL_KEYS.USER_DETAILS);
    if (userDetails?.acctType === "RECRUITER") {
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) return;
    const loadExecutives = async () => {
      try {
        const executives = await fetchExecutives();
        const map = {};
        executives.forEach(exec => {
          map[exec.id] = exec.fullName; // Assuming executives have id and fullName
        });
        setExecutiveMap(map);
      } catch (error) {
        console.error("Failed to load executives for mapping:", error);
      }
    };
    loadExecutives();
  }, []);

  // Automatically fetch clients on initial load
  useEffect(() => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      setLoading(false);
      return;
    }

    // Skip API call for recruiters
    const userDetails = parseLocalStorageJSON(LOCAL_KEYS.USER_DETAILS);
    if (userDetails?.acctType === "RECRUITER") {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('access_token');
    if (token) {
      fetchClients();
    }
  }, []);

  return (
    <ClientContext.Provider
      value={{
        clients,
        client,
        submissions,
        loading,
        error, // Provide error state to be accessed by consumers
        addNewClient,
        updateExistingClient,
        fetchClients,
        fetchClientById,
        fetchFilteredSubmissions,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

// Custom hook for consuming the contextexport const useClientContext = () => useContext(ClientContext);